import { css } from "@emotion/react";
import React, { useEffect, useRef, useState, useCallback } from "react";
import useMeasure, { type RectReadOnly } from "react-use-measure";
import { CSS_MQ } from "../../../../styles/breakpoints";
import { NavigationBar } from "./NavigationBar";
import { useNavigation } from "./useNavigation";
import isEqual from "react-fast-compare";

const Measure: React.FC<{
  children: (ref: HTMLOrSVGElement | null) => React.ReactNode;
  onBoundsChange: (bounds: RectReadOnly) => void;
}> = ({ children, onBoundsChange }) => {
  const [ref, bounds] = useMeasure();
  const [sentBounds, setSentBounds] = useState<typeof bounds | null>(null);

  useEffect(() => {
    if (!isEqual(bounds, sentBounds)) {
      onBoundsChange(bounds);
      setSentBounds(bounds);
    }
  }, [bounds]);

  useEffect(() => {
    setTimeout(() => {
      window.dispatchEvent(new Event("resize"));
    }, 1);
  }, []);

  return <>{children(ref as any)}</>;
};

export function CardsCarousel2<T>({
  items,
  Component,
  getKey,
  theme,
}: {
  items: Array<T>;
  Component: React.ComponentType<{
    item: T;
    isActive?: boolean;
    ref?: (element: HTMLOrSVGElement) => void;
    minHeight: number;
  }>;
  getKey: (t: T) => string;
  theme: "dark-background" | "light-background";
}) {
  const ref = useRef<HTMLDivElement>(null);
  const { handlers, onPrevious, onNext, visibleIdx, setVisibleIdx, shouldPassThroughWalls } = useNavigation(items, ref);

  const [bounds, setBounds] = useState<Array<RectReadOnly>>([]);

  const onBoundsChange = (idx: number) => (bound: RectReadOnly) => {
    setBounds(prev => {
      const newBounds = [...prev];
      newBounds[idx] = bound;
      return newBounds;
    });
  };

  const effectiveMaxHeight = Math.max(...bounds.map(b => b.height));

  const nodes = items.map((el, idx) => {
    const minHeight = Math.max(...bounds.filter((_, idx2) => idx2 !== idx).map(b => b.height));

    const style = {
      transform: `translateX(calc(${100 * idx}% + ${idx} * var(--g)))`,
      minHeight: `${minHeight}px`,
    };

    const isActive = idx === visibleIdx;
    const isClickable = items.length > 1 && !isActive;

    return (
      <Measure onBoundsChange={onBoundsChange(idx)} key={getKey(el)}>
        {ref => (
          <div
            tabIndex={0}
            onClick={isClickable ? () => setVisibleIdx(idx) : undefined}
            key={getKey(el)}
            css={cardWrapper}
            style={style}
          >
            <Component item={el} isActive={isActive} ref={ref as any} minHeight={minHeight} />
          </div>
        )}
      </Measure>
    );
  });

  return (
    <>
      <div
        {...handlers}
        css={css`
          width: 100%;
          ${CSS_MQ.atLeastMd} {
            width: 80%;
          }

          margin: 0 auto;

          position: relative;
          transition: 0.3s ease-out;
        `}
        style={{
          transform: `translateX(calc(${-100 * visibleIdx}% - ${visibleIdx * 40}px))`,
          minHeight: `${effectiveMaxHeight}px`,
          display: "flex",
        }}
      >
        {nodes}
      </div>

      {items.length > 1 && (
        <NavigationBar
          theme={theme}
          onPrevious={onPrevious}
          onNext={onNext}
          shouldPassThroughWalls={shouldPassThroughWalls}
          visibleIdx={visibleIdx}
          setVisibleIdx={setVisibleIdx}
          items={items}
        />
      )}
    </>
  );
}

const cardWrapper = css`
  cursor: pointer;
  position: absolute;

  --g: 40px;

  width: 100%;
  height: 100%;

  top: 0;
  left: 0;

  z-index: 1;

  // display: flex;
`;
